import {
  buildWhenRangeValue,
  instantToZonedIso,
  zonedWallClockToInstant,
} from '@littlebird/action-cards';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/calendar';
import { Command } from '@/components/ui/cmdk/cmdk';
import { XStack } from '@/components/ui/layout/layout';
import { Popover } from '@/components/ui/popover';
import { TimePickerPopover } from '@/components/ui/TimePickerPopover';
import { cn } from '@/lib/utils';

// Triggers are one text line tall (same leading as the field box) instead of a
// fixed button height, so the When row is as tall in edit mode as the text it
// replaces. TimePickerPopover only exposes a class hook, hence the second constant.
const TRIGGER_HEIGHT_CLASS = 'h-[1lh] py-0 leading-relaxed';
const TIME_TRIGGER_CLASS = `${TRIGGER_HEIGHT_CLASS} w-auto rounded-md px-2`;

const DURATION_OPTIONS_MINUTES = [15, 30, 45, 60, 90, 120, 180, 240, 480];

const formatDurationLabel = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  if (Number.isInteger(hours)) return `${hours} hour${hours === 1 ? '' : 's'}`;
  return `${Math.floor(hours)}h ${minutes % 60}min`;
};

// 'YYYY-MM-DD', read off the Date's local fields — the value is a calendar day
// picked in the UI, not an instant, so there is no timezone to convert through.
const toDateValue = (date: Date): string => {
  const year = date.getFullYear().toString().padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type DurationPopoverProps = {
  durationMinutes: number;
  onDurationSelected: (minutes: number) => void;
};

const DurationPopover = ({
  durationMinutes,
  onDurationSelected,
}: DurationPopoverProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="outline"
          size="xs"
          className={cn(TRIGGER_HEIGHT_CLASS, 'justify-start gap-2')}
        >
          <span className="truncate">
            {formatDurationLabel(durationMinutes)}
          </span>
        </Button>
      </Popover.Trigger>
      <Popover.Content className="mt-1 w-40 p-0" align="start">
        <Command.Root>
          <Command.List className="max-h-72 overflow-y-auto">
            <Command.Group>
              {DURATION_OPTIONS_MINUTES.map((minutes) => (
                <Command.Item
                  key={minutes}
                  value={formatDurationLabel(minutes)}
                  data-selected={minutes === durationMinutes}
                  onSelect={() => {
                    onDurationSelected(minutes);
                    setOpen(false);
                  }}
                >
                  <span className="flex-1 truncate text-sm">
                    {formatDurationLabel(minutes)}
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command.Root>
      </Popover.Content>
    </Popover.Root>
  );
};

const TIMEZONE_OPTIONS = Intl.supportedValuesOf('timeZone');

type TimezonePopoverProps = {
  timezone: string;
  onTimezoneSelected: (timezone: string) => void;
};

// Raw IANA names (e.g. 'Asia/Kolkata') rather than a friendlier label: unambiguous,
// and it's what the Command input's built-in fuzzy filter searches against.
const TimezonePopover = ({
  timezone,
  onTimezoneSelected,
}: TimezonePopoverProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="outline"
          size="xs"
          className={cn(TRIGGER_HEIGHT_CLASS, 'max-w-40 justify-start gap-2')}
        >
          <span className="truncate">{timezone}</span>
        </Button>
      </Popover.Trigger>
      <Popover.Content className="mt-1 w-64 p-0" align="start">
        <Command.Root>
          <Command.Input placeholder="Search time zones…" />
          <Command.Empty className="px-4 py-6 text-xs">
            No time zones found.
          </Command.Empty>
          <Command.List className="max-h-72 overflow-y-auto">
            <Command.Group>
              {TIMEZONE_OPTIONS.map((tz) => (
                <Command.Item
                  key={tz}
                  value={tz}
                  data-selected={tz === timezone}
                  onSelect={() => {
                    onTimezoneSelected(tz);
                    setOpen(false);
                  }}
                >
                  <span className="flex-1 truncate text-sm">{tz}</span>
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command.Root>
      </Popover.Content>
    </Popover.Root>
  );
};

type DatePopoverProps = {
  date: string;
  label: string;
  timezone: string;
  onDateSelected: (date: string) => void;
};

const DatePopover = ({
  date,
  label,
  timezone,
  onDateSelected,
}: DatePopoverProps) => {
  const [open, setOpen] = useState(false);
  const selectedDate = new Date(`${date}T00:00:00`);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="outline"
          size="xs"
          className={cn(TRIGGER_HEIGHT_CLASS, 'gap-2')}
          aria-label={`${label} (${timezone})`}
        >
          <Icons.calendar className="size-3.5" />
          <span className="truncate">{label}</span>
        </Button>
      </Popover.Trigger>
      <Popover.Content className="mt-1 w-auto p-0" align="start">
        <div className="p-3">
          <Calendar
            mode="single"
            selected={selectedDate}
            // without this the popover opens on the current month, not the event's
            defaultMonth={selectedDate}
            onSelect={(nextDate?: Date) => {
              if (!nextDate) return;
              onDateSelected(toDateValue(nextDate));
              setOpen(false);
            }}
            autoFocus
          />
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

export type DateTimeRangeEditorProps = {
  range: { start: string; end: string; timezone: string };
  onChange: (value: string) => void;
};

/**
 * Edits a calendar event's start time, duration, and timezone. Date/time/duration
 * changes keep the current timezone; a timezone change keeps the wall-clock digits
 * and reinterprets them in the new zone (Google Calendar's own convention), so the
 * event moves in absolute time but reads the same on the clock.
 */
export const DateTimeRangeEditor = ({
  range,
  onChange,
}: DateTimeRangeEditorProps) => {
  const startIso = instantToZonedIso(new Date(range.start), range.timezone);
  const date = startIso.slice(0, 10);
  const time = startIso.slice(11, 16);
  const durationMs =
    new Date(range.end).getTime() - new Date(range.start).getTime();
  const dateLabel = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: range.timezone,
  }).format(new Date(range.start));

  const applyChange = (
    nextDate: string,
    nextTime: string,
    nextDurationMs: number,
    nextTimeZone: string
  ) => {
    const instant = zonedWallClockToInstant(
      `${nextDate}T${nextTime}`,
      nextTimeZone
    );
    onChange(
      buildWhenRangeValue(range, instant, {
        durationMs: nextDurationMs,
        timezone: nextTimeZone,
      })
    );
  };

  return (
    <XStack className="min-w-0 flex-1 flex-wrap items-center gap-1.5">
      <DatePopover
        date={date}
        label={dateLabel}
        timezone={range.timezone}
        onDateSelected={(nextDate) =>
          applyChange(nextDate, time, durationMs, range.timezone)
        }
      />
      <TimePickerPopover
        selectedTime={time}
        triggerAriaLabel={`Start time (${range.timezone})`}
        triggerClassName={TIME_TRIGGER_CLASS}
        onTimeSelected={(nextTime) =>
          applyChange(date, nextTime, durationMs, range.timezone)
        }
      />
      <DurationPopover
        durationMinutes={Math.round(durationMs / 60_000)}
        onDurationSelected={(minutes) =>
          applyChange(date, time, minutes * 60_000, range.timezone)
        }
      />
      <TimezonePopover
        timezone={range.timezone}
        onTimezoneSelected={(nextTimeZone) =>
          applyChange(date, time, durationMs, nextTimeZone)
        }
      />
    </XStack>
  );
};
